/**
 * DD outcome data layer (Phase A) — persists Due Diligence conclusions to the
 * canonical `dd_reports` table and reads the real "completed DD" list. Replaces
 * the prior in-memory-only `addDueDiligenceConclusion` (which never hit the DB,
 * so the list was always empty in production → mock fallback).
 *
 * `investorUserId` is the auth.users id (== auth.uid()), matching dd_reports RLS.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { rpc } from '@/lib/supabase/rpc';
import { fetchInvestorScoreEvents } from '@/lib/supabase/investor-score-events';
import { queryKeys } from '@/lib/supabase/api';
import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import { scoreSnapshotAt, scoreSnapshotPinned, durationDays, countHighSeverityRisks, dataRoomSummary } from './dd-report-helpers';
import { assembleDDMetrics, ddRichExtractionOf } from './dd-metrics';
import { mapSharedReportRow, type SharedReportRow, type SharedReportView } from './shared-report';
import { normalizeDDDecision, type DDDecision } from './decision';
import type { CompletedDD } from '@/lib/supabase/investor-queries';
import type { ScoreEventReadModel } from '@/lib/supabase/edge-functions';

// dd_reports + score_event aren't in the generated types union here; cast for .from().
const db = supabase as any;

// The decision vocabulary lives in its own pure module (./decision) so it can be
// tested without the Supabase client this file imports. Re-exported here because
// it is part of this module's public surface and callers already import it here.
export { normalizeDDDecision, type DDDecision };

export const ddReportKeys = {
  completed: (investorUserId: string) => ['dd-reports', 'completed', investorUserId] as const,
  sharedForStartup: (startupId: string) => ['dd-reports', 'shared-for-startup', startupId] as const,
  dataRoomPass: (reportId: string) => ['dd-reports', 'data-room-pass', reportId] as const,
};

/**
 * Real completed-DD list for an investor, with before/after score snapshots
 * derived from the startup's score_event history (by started/concluded dates).
 */
export function useCompletedDDReports(investorUserId: string | undefined) {
  return useQuery({
    queryKey: ddReportKeys.completed(investorUserId || ''),
    queryFn: async (): Promise<CompletedDD[]> => {
      if (!investorUserId || mode.isMock || !isSupabaseConfigured()) return [];

      const { data: reports, error } = await db
        .from('dd_reports')
        .select(
          'id, startup_id, startup_name, startup_industry, startup_stage, decision, amount, conclusion_notes, started_at, concluded_at, documents_reviewed_count, executive_summary, investment_thesis, key_risks, notable_points, narrative_status, shared_with_startup, shared_at, score_event_end_id',
        )
        .eq('investor_user_id', investorUserId)
        .eq('status', 'concluded')
        .order('concluded_at', { ascending: false });
      if (error) throw error;

      const rows = (reports ?? []) as any[];
      if (rows.length === 0) return [];

      const startupIds = [...new Set(rows.map((r) => r.startup_id))];

      // Pull score history (extraction metrics + risks) and the founder profiles
      // for every startup in two batched queries. Score events come from the
      // investor read path (investors have no SELECT on score_event since
      // 20261018130000): the raw payload with a data-room grant, the numeric DD
      // metrics without one — ddRichExtractionOf reads either. A failed score
      // read is reported and degrades to "no metrics", as before.
      const [events, { data: profiles }] = await Promise.all([
        fetchInvestorScoreEvents(startupIds)
          .then((rows) =>
            [...rows].sort((a, b) => new Date(a.computed_at).getTime() - new Date(b.computed_at).getTime()),
          )
          .catch((scoreError: unknown) => {
            reportError(scoreError, { context: 'useCompletedDDReports.scoreFetch', silent: true });
            return [];
          }),
        db
          .from('startup_profiles')
          .select('id, monthly_revenue, user_count, growth_rate, team_size, market_size, funding_target')
          .in('id', startupIds),
      ]);

      type EventRow = {
        id: string;
        computedAt: string;
        composite: ScoreEventReadModel['composite'];
        inputPayload: unknown;
        risks: { severity?: string | null }[] | null;
      };
      const byStartup = new Map<string, EventRow[]>();
      for (const e of events) {
        const list = byStartup.get(e.startup_id) ?? [];
        list.push({
          id: e.id,
          computedAt: e.computed_at,
          composite: e.composite,
          inputPayload: e.input_payload ?? null,
          risks: e.risks ?? null,
        });
        byStartup.set(e.startup_id, list);
      }

      const profileById = new Map<string, Record<string, number | null>>();
      for (const p of (profiles ?? []) as any[]) profileById.set(p.id, p);

      /** Latest event at or before `atIso` (ascending list). */
      const eventAsOf = (list: EventRow[], atIso: string | null): EventRow | null => {
        if (!atIso) return null;
        const at = new Date(atIso).getTime();
        if (Number.isNaN(at)) return null;
        let chosen: EventRow | null = null;
        for (const e of list) {
          if (new Date(e.computedAt).getTime() <= at) chosen = e;
          else break;
        }
        return chosen;
      };

      return rows.map((r) => {
        const hist = byStartup.get(r.startup_id) ?? [];
        const start = scoreSnapshotAt(hist, r.started_at);
        // DD Phase B: prefer the pinned end event (the dd_concluded rescore);
        // temporal derivation stays as the fallback for pre-Phase-B reports.
        const end = scoreSnapshotPinned(hist, r.score_event_end_id ?? null, r.concluded_at);

        // Metrics + flagged issues read from the as-of-conclusion score event —
        // the evidence base reviewed during this DD.
        const asOf = eventAsOf(hist, r.concluded_at) ?? hist[hist.length - 1] ?? null;
        const rich = ddRichExtractionOf(asOf?.inputPayload);
        const metrics = assembleDDMetrics(profileById.get(r.startup_id), rich);
        const flaggedIssues = countHighSeverityRisks(asOf?.risks);

        return {
          id: r.id,
          startupId: r.startup_id,
          startupName: r.startup_name ?? 'Unknown',
          industry: r.startup_industry ?? '',
          stage: r.startup_stage ?? '',
          decision: r.decision ?? 'undecided',
          amount: r.amount ?? null,
          notes: r.conclusion_notes ?? null,
          completedAt: r.concluded_at ?? null,
          startDate: r.started_at ?? null,
          endDate: r.concluded_at ?? undefined,
          durationDays: durationDays(r.started_at, r.concluded_at) ?? undefined,
          documentsReviewed: r.documents_reviewed_count ?? 0,
          flaggedIssues,
          dataRoomVersion: dataRoomSummary(r.documents_reviewed_count, r.concluded_at),
          finalComments: r.conclusion_notes ?? undefined,
          metrics: metrics.length > 0 ? metrics : undefined,
          // Narrative (Phase C.2 — LLM draft, investor-reviewed). Undefined until generated.
          executiveSummary: r.executive_summary ?? undefined,
          investmentThesis: r.investment_thesis ?? undefined,
          keyRisks: r.key_risks ?? undefined,
          notablePoints: Array.isArray(r.notable_points) ? r.notable_points : undefined,
          narrativeStatus: r.narrative_status ?? 'none',
          sharedWithStartup: r.shared_with_startup ?? false,
          sharedAt: r.shared_at ?? undefined,
          scoresAtStart: start ?? undefined,
          scoresAtEnd: end ?? undefined,
        };
      });
    },
    enabled: !!investorUserId,
    staleTime: 5 * 60 * 1000,
  });
}

interface StartDDParams {
  investorUserId: string;
  startupId: string;
  startupName: string;
  industry?: string | null;
  stage?: string | null;
  sharedDdId?: string | null;
}

/**
 * What starting a DD came back with: the report, or — when the investor holds no
 * data-room pass for this company — the fact that the DD needs one. `null` in
 * mock mode, where nothing is persisted.
 */
export type StartDDResult = { id: string } | { needsDataRoom: true } | null;

/**
 * Mark a DD as started (idempotent). Creates an `in_progress` dd_reports row the
 * first time an investor opens DD for a startup — this fixes the "before" score
 * anchor (started_at) and never resets it on re-open.
 *
 * A DD needs the data-room pass (owner rule, 2026-10-23): the INSERT policy
 * requires `has_data_room_access` (20261023120000) and answers 42501 without it.
 * That refusal is a STATE of this screen — the investor is shown the request
 * path — so it resolves `{ needsDataRoom: true }` instead of throwing. An
 * EXISTING report is returned as before, whatever happened to the pass since:
 * the report stays the investor's (owner decision #8).
 */
export function useStartDD() {
  return useMutation({
    mutationFn: async (p: StartDDParams): Promise<StartDDResult> => {
      if (mode.isMock || !isSupabaseConfigured()) return null;

      const { data: existing } = await db
        .from('dd_reports')
        .select('id')
        .eq('investor_user_id', p.investorUserId)
        .eq('startup_id', p.startupId)
        .maybeSingle();
      if (existing) return existing; // preserve original started_at

      const { data, error } = await db
        .from('dd_reports')
        .insert({
          investor_user_id: p.investorUserId,
          startup_id: p.startupId,
          startup_name: p.startupName,
          startup_industry: p.industry ?? null,
          startup_stage: p.stage ?? null,
          shared_dd_id: p.sharedDdId ?? null,
          status: 'in_progress',
        })
        .select('id')
        .single();
      if ((error as { code?: string } | null)?.code === '42501') {
        return { needsDataRoom: true };
      }
      if (error) {
        // B20: the existence check above and this insert aren't atomic — a concurrent
        // start can win the race and take the UNIQUE(investor_user_id, startup_id) slot.
        // Treat that unique violation as success and return the existing row, so its
        // started_at (the before-anchor) is preserved rather than throwing.
        if ((error as { code?: string }).code === '23505') {
          const { data: raced } = await db
            .from('dd_reports')
            .select('id')
            .eq('investor_user_id', p.investorUserId)
            .eq('startup_id', p.startupId)
            .maybeSingle();
          if (raced) return raced;
        }
        throw error;
      }
      return data;
    },
    onError: (err) => reportError(err, { context: 'useStartDD', level: 'warning', silent: true }),
  });
}

/**
 * Is this DD report backed by the data room — may its narrative be generated?
 * Asks `dd_report_data_room_pass` (20261023120000), the ONE definition, which
 * generate-dd-report also checks: a live pass, or the pass the investor's own
 * conclusion ended. The server stays the authority; this only lets the screen
 * offer the request path instead of a Generate button that would be refused.
 * A failed read is an ERROR (the caller keeps the button), never a silent "no".
 */
export function useDDReportDataRoomPass(reportId: string | null | undefined) {
  return useQuery({
    queryKey: ddReportKeys.dataRoomPass(reportId ?? ''),
    enabled: !!reportId && !mode.isMock && isSupabaseConfigured(),
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await rpc('dd_report_data_room_pass', { p_report_id: reportId as string });
      if (error) throw error;
      return data === true;
    },
  });
}

interface ConcludeDDParams {
  /**
   * The `dd_reports` row `useStartDD` created when this DD was opened. The RPC
   * keys off it — concluding is an update of an existing diligence, never the
   * creation of one, so a missing id is an honest failure rather than a new row.
   */
  reportId: string | null;
  investorUserId: string;
  startupId: string;
  decision: DDDecision;
  /** Required for `invest`: the RPC refuses to invent an investment figure. */
  amount?: number | null;
  notes?: string | null;
}

/**
 * Persist a DD conclusion AND move the deal — one `conclude_dd_report` call.
 *
 * This used to write `dd_reports` and stop, which meant an investor could
 * conclude `invest` while the dealflow card stayed exactly where it was and the
 * portfolio never heard about it. The RPC now writes the report, sets
 * `dealflow.status` (invest → invested, pass → rejected, monitor → monitoring)
 * and creates the portfolio holding in ONE transaction; three client writes
 * could never have been made atomic, and a deal that is invested in one table
 * and screening in another is worse than either.
 */
export function useConcludeDD() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: ConcludeDDParams) => {
      if (mode.isMock || !isSupabaseConfigured()) return null;

      if (!p.reportId) {
        throw new Error('This due-diligence session has not finished loading. Please try again in a moment.');
      }

      // ARCH-06: supabase-js resolves with { error } rather than throwing, so
      // the result is bound and checked — awaiting alone would swallow it.
      const { error } = await rpc('conclude_dd_report', {
        p_report_id: p.reportId,
        p_decision: normalizeDDDecision(p.decision),
        // Both are `DEFAULT NULL` in the function signature (verified against the
        // live catalogue), so omitting the key lets Postgres apply the default —
        // which is what `?? null` was expressing before the generated types
        // became visible here.
        p_amount: p.amount ?? undefined,
        p_notes: p.notes ?? undefined,
      });
      if (error) throw error;
      // The RPC echoes the report id back; return it in the shape callers read.
      return { id: p.reportId };
    },
    onSuccess: (_data, p) => {
      qc.invalidateQueries({ queryKey: ddReportKeys.completed(p.investorUserId) });

      // The RPC moved the deal and (on invest) created the holding, so the two
      // surfaces that read those tables are now stale. Without this the card
      // still renders at its old stage until the next cold load — which looks
      // exactly like the defect the RPC was written to fix. Both keys are
      // invalidated by PREFIX: `dealflow` is keyed on the investor_profiles id
      // and `portfolio` on the auth user id, and only the latter is in scope
      // here (see queryKeys in lib/supabase/api.ts).
      qc.invalidateQueries({ queryKey: ['dealflow'] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });

      // The GRANT changed too, on `pass` and `monitor` (20260806090000). It is
      // a separate cache entry from the stage — `useMyDataRoomGrant` reads
      // `data_room_access` directly — and it feeds the messaging gate, so
      // without this the "Message founder" button keeps rendering from a grant
      // the server revoked a moment ago. Invalidated unconditionally rather
      // than only on pass/monitor: an `invest` conclusion refetches an
      // unchanged row, which costs one query and cannot be wrong, whereas
      // branching here would put the RPC's rule in a second place.
      qc.invalidateQueries({ queryKey: queryKeys.myDataRoomGrant(p.startupId) });

      // DD Phase B: fire the evidence-driven rescore (reason dd_concluded).
      // Fire-and-forget — conclusion UX never blocks on the LLM. On completion
      // the fn has pinned score_event_end_id, so refresh the completed list to
      // pick up the real before/after delta. Failure is tolerable: the report
      // keeps its temporal-derivation fallback.
      if (!mode.isMock && isSupabaseConfigured()) {
        db.functions
          .invoke('rescore-on-evidence', {
            body: { startupId: p.startupId, reason: 'dd_concluded' },
          })
          .then(() => {
            qc.invalidateQueries({ queryKey: ddReportKeys.completed(p.investorUserId) });
          })
          .catch((err: unknown) => {
            reportError(err, { context: 'useConcludeDD.rescoreOnEvidence', level: 'warning', silent: true });
          });
      }
    },
    onError: (err) => reportError(err, { context: 'useConcludeDD', level: 'warning' }),
  });
}

/**
 * Phase D — investor opts in to share (or unshare) a completed report with the
 * startup. RLS scopes the update to the owning investor.
 */
export function useShareDDReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { ddReportId: string; shared: boolean }) => {
      if (mode.isMock || !isSupabaseConfigured()) return;
      const { error } = await db
        .from('dd_reports')
        .update({ shared_with_startup: p.shared, shared_at: p.shared ? new Date().toISOString() : null })
        .eq('id', p.ddReportId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dd-reports'] }),
    onError: (err) => reportError(err, { context: 'useShareDDReport' }),
  });
}

/**
 * Phase D — founder view: DD reports investors have shared with this startup.
 * Reads via the `get_shared_dd_reports()` SECURITY DEFINER function (safe columns
 * only — never the investor's amount/conclusion_notes), then attaches the named
 * investor + before/after scores + metrics derived from the startup's own
 * score_event/profile (the founder can read their own startup's data).
 */
export function useSharedDDReportsForMyStartup(startupId: string | undefined) {
  return useQuery({
    queryKey: ddReportKeys.sharedForStartup(startupId || ''),
    queryFn: async (): Promise<SharedReportView[]> => {
      if (!startupId || mode.isMock || !isSupabaseConfigured()) return [];

      const { data: rows, error } = await rpc('get_shared_dd_reports');
      if (error) throw error;
      const reportRows = (rows ?? []) as SharedReportRow[];
      if (reportRows.length === 0) return [];

      // Investor name/firm now come from the get_shared_dd_reports() RPC itself
      // (SECURITY DEFINER, so it resolves them server-side even though E-11
      // removed the founder's direct read on investor_profiles). The previous
      // client join here matched investor_profiles.id against dd_reports.investor_user_id
      // (an auth uid) and always returned empty — the name was already blank.
      const [{ data: events }, { data: profile }] = await Promise.all([
        db
          .from('score_event')
          .select('id, computed_at, composite, input_payload, risks')
          .eq('startup_id', startupId)
          .order('computed_at', { ascending: true }),
        db
          .from('startup_profiles')
          .select('monthly_revenue, user_count, growth_rate, team_size, market_size, funding_target')
          .eq('id', startupId)
          .maybeSingle(),
      ]);

      type Ev = { id: string; computedAt: string; composite: ScoreEventReadModel['composite']; inputPayload: unknown };
      const hist: Ev[] = ((events ?? []) as any[]).map((e) => ({
        id: e.id,
        computedAt: e.computed_at,
        composite: e.composite,
        inputPayload: e.input_payload ?? null,
      }));
      const eventAsOf = (atIso: string | null): Ev | null => {
        if (!atIso) return null;
        const at = new Date(atIso).getTime();
        if (Number.isNaN(at)) return null;
        let chosen: Ev | null = null;
        for (const e of hist) {
          if (new Date(e.computedAt).getTime() <= at) chosen = e;
          else break;
        }
        return chosen;
      };
      const profileFields = (profile ?? null) as Record<string, number | null> | null;

      return reportRows.map((r) => {
        const view = mapSharedReportRow(r, { name: r.investor_name, firm: r.investor_firm });
        view.scoresAtStart = scoreSnapshotAt(hist, r.started_at) ?? undefined;
        // DD Phase B: same pin preference as the investor view, so both sides
        // of a shared report show the identical "after" score.
        view.scoresAtEnd = scoreSnapshotPinned(hist, r.score_event_end_id ?? null, r.concluded_at) ?? undefined;
        const asOf = eventAsOf(r.concluded_at) ?? hist[hist.length - 1] ?? null;
        const metrics = assembleDDMetrics(profileFields, ddRichExtractionOf(asOf?.inputPayload));
        view.metrics = metrics.length > 0 ? metrics : undefined;
        return view;
      });
    },
    enabled: !!startupId,
    staleTime: 60 * 1000,
  });
}
